import { XIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useOne, useUpdate } from "@refinedev/core";
import Button from "components/button";
import { FullScreenLoading } from "components/fullScreenLoading";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal";
import RadioButton from "components/radioButton";
import { ISeatLayoutData, ISeatLayoutInfoForm,  ISimpleEventDetail,  SeatItem,  SeatLayoutPosition,  SeatLayoutStatus } from "interfaces";
import { get, isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import { SEAT_POSITION, SEAT_ROW_NAME_TYPES, SEAT_STATUS, WALKWAY_VALUE } from "utills/constants";
import { z } from "zod";
import { SeatmapTable } from "../seatLayoutTable";
import { isOpenRegistration } from "utills/helpers";
import Select from "components/select";

interface SeatLayoutInfoModalProps extends IModalProps {
  id?: string;
  eventDetailData: ISimpleEventDetail;
  onSubmit: (data: ISeatLayoutInfoForm) => void;
}

const SeatLayoutInfoSchema = () => {
  return z.object({
    layout_name: z.string().min(1, "This field is required"),
    row_name_type: z.string().min(1, "This field is required"),
    row_name_position: z.string().min(1, "This field is required"),
    number_of_columns: z.number({
      invalid_type_error: "Number of columns must be a number",
      required_error: "Number of columns is required",
    }),
    number_of_rows: z.number({
      invalid_type_error: "Number of rows must be a number",
      required_error: "Number of rows is required",
    }),
  }); 
}

export const UpdateSeatLayoutInfoModal = ({
  open,
  onClose,
  id,
  eventDetailData,
  onSubmit,
}: SeatLayoutInfoModalProps) => {
  const { mutate: updateMutation, isLoading: isUpdating } = useUpdate();

  const { data: seatLayoutDetail, isFetching: isFetchingLayoutDetail, isLoading: isLoadingLayoutDetail, refetch } = useOne<ISeatLayoutData, HttpError>({
    resource: "content/seat-maps/layout-seat",
    id: id ?? "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(id && open),
    } as any,
    successNotification: (data, values, resource) => {
      const responseData = get(data, "data", null);
      reset({
        row_name_type: responseData?.row_name_type ?? "alphabet",
        row_name_position: responseData?.row_name_position ?? "left_top_down",
        layout_name: responseData?.layout_name ?? "",
        number_of_columns: responseData?.number_of_columns ?? 1,
        number_of_rows: responseData?.number_of_rows ?? 1,  
      })
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    }
  });
  
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<ISeatLayoutInfoForm, HttpError, ISeatLayoutInfoForm>({
    defaultValues: {
      row_name_type: "alphabet",
      row_name_position: "left_top_down",
      layout_name: "",
      number_of_columns: 1,
      number_of_rows: 1,
    },
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        SeatLayoutInfoSchema()
      );
      return createResolver(values, context, options);
    },
  });

  const postionSelected = watch("row_name_position");

  const [seats, setSeats] = useState<SeatItem[]>([]);

  useEffect(() => {
    if (!isFetchingLayoutDetail && seatLayoutDetail?.data?.seats) setSeats(seatLayoutDetail?.data?.seats);
  }, [isFetchingLayoutDetail]);

  const handleResetWalkway = () => {
    const resetWalkwaySeats = seats?.map(item => ({
      ...item,
      walkway: []
    }));

    setSeats(resetWalkwaySeats);

    handleSubmit((values) => submitResetLayout(values, resetWalkwaySeats))();
  }

  const handleRemoveWalkway = (seatId: string, position: "left" | "right") => {
    const selectedSeatIndex = seats?.findIndex(seat => seat.id === seatId);
    const selectedSeat = seats[selectedSeatIndex];

    console.log({ seatId, selectedSeat, position });

    if (!selectedSeat) {
      toast.error("Remove seat walkway failed");
      return;
    }

    const newWalkwayValue = selectedSeat?.walkway?.filter((pos: string) => {
      if (!pos?.includes(position)) return pos;
    })

    updateMutation(
      {
        resource: "content/seat-maps/seats",
        id: seatId,
        meta: {
          ignoreResourceWrapper: true
        },
        values: {
          status: selectedSeat?.status,
          walkway: newWalkwayValue
        },
      },
      {
        onSuccess: (data) => {
          refetch();
          toast.success("Update seat status successfully");
        },
      }
    );
  }

  useEffect(() => {
    if (open && isEmpty(id)) {
      reset({
        row_name_type: "alphabet",
        row_name_position: "left_top_down",
        layout_name: "",
        number_of_columns: 1,
        number_of_rows: 1,  
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const _submit = (values: ISeatLayoutInfoForm) => {
    onSubmit(values);
  };

  const submitResetLayout = (values: ISeatLayoutInfoForm, resetWalkwaySeats: SeatItem[]) => {
    const formatValue = {
      ...values,
      seats: resetWalkwaySeats
    }

    onSubmit(formatValue);
  };

  console.log(errors);

  const _updateSeatStatus = (status: SeatLayoutStatus, seatId: string) => {
    const selectedSeatIndex = seats?.findIndex(seat => seat.id === seatId);
    const selectedSeat = seats[selectedSeatIndex];
    const position = watch("row_name_position");

    if (!selectedSeat) {
      toast.error("Add seat walkway failed");
      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.LEFT_TOP_DOWN || position === SeatLayoutPosition.LEFT_BOTTOM_UP) &&
      status === SeatLayoutStatus.WALKWAY_ON_THE_LEFT
    ) {
      const updated = [...seats];

      const seat = { ...updated[selectedSeatIndex] };
      seat.walkway = [...(seat.walkway ?? [])];

      if (!seat.walkway?.includes(WALKWAY_VALUE.IN_LEFT)) {
        seat.walkway?.push(WALKWAY_VALUE.IN_LEFT);
      }

      updated[selectedSeatIndex] = seat;

      // setSeats(updated);

      updateMutation(
        {
          resource: "content/seat-maps/seats",
          id: seatId,
          meta: {
            ignoreResourceWrapper: true
          },
          values: {
            status: selectedSeat?.status,
            walkway: updated[selectedSeatIndex]?.walkway
          },
        },
        {
          onSuccess: (data) => {
            refetch();
            toast.success("Add seat walkway successfully");
          },
        }
      );

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.LEFT_TOP_DOWN || position === SeatLayoutPosition.LEFT_BOTTOM_UP) &&
      status === SeatLayoutStatus.WALKWAY_ON_THE_RIGHT
    ) {
      const updated = [...seats];

      const seat = { ...updated[selectedSeatIndex] };
      seat.walkway = [...(seat.walkway ?? [])];

      if (!seat.walkway?.includes(WALKWAY_VALUE.IN_RIGHT)) {
        seat.walkway?.push(WALKWAY_VALUE.IN_RIGHT);
      }

      updated[selectedSeatIndex] = seat;

      // setSeats(updated);

      updateMutation(
        {
          resource: "content/seat-maps/seats",
          id: seatId,
          meta: {
            ignoreResourceWrapper: true
          },
          values: {
            status: selectedSeat?.status,
            walkway: updated[selectedSeatIndex]?.walkway
          },
        },
        {
          onSuccess: (data) => {
            refetch();
            toast.success("Add seat walkway successfully");
          },
        }
      );

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.RIGHT_TOP_DOWN || position === SeatLayoutPosition.RIGHT_BOTTOM_UP) &&
      status === SeatLayoutStatus.WALKWAY_ON_THE_LEFT
    ) {
      const updated = [...seats];

      const seat = { ...updated[selectedSeatIndex] };
      seat.walkway = [...(seat.walkway ?? [])];

      if (!seat.walkway.includes(WALKWAY_VALUE.IN_LEFT)) {
        seat.walkway.push(WALKWAY_VALUE.IN_LEFT);
      }

      updated[selectedSeatIndex] = seat;

      // setSeats(updated);

      updateMutation(
        {
          resource: "content/seat-maps/seats",
          id: seatId,
          meta: {
            ignoreResourceWrapper: true
          },
          values: {
            status: selectedSeat?.status,
            walkway: updated[selectedSeatIndex]?.walkway
          },
        },
        {
          onSuccess: (data) => {
            refetch();
            toast.success("Add seat walkway successfully");
          },
        }
      );

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.RIGHT_TOP_DOWN || position === SeatLayoutPosition.RIGHT_BOTTOM_UP) &&
      status === SeatLayoutStatus.WALKWAY_ON_THE_RIGHT
    ) {
      const updated = [...seats];

      const seat = { ...updated[selectedSeatIndex] };
      seat.walkway = [...(seat.walkway ?? [])];

      if (!seat.walkway.includes(WALKWAY_VALUE.IN_RIGHT)) {
        seat.walkway.push(WALKWAY_VALUE.IN_RIGHT);
      }

      updated[selectedSeatIndex] = seat;

      // setSeats(updated);

      updateMutation(
        {
          resource: "content/seat-maps/seats",
          id: seatId,
          meta: {
            ignoreResourceWrapper: true
          },
          values: {
            status: selectedSeat?.status,
            walkway: updated[selectedSeatIndex]?.walkway
          },
        },
        {
          onSuccess: (data) => {
            refetch();
            toast.success("Add seat walkway successfully");
          },
        }
      );

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.TOP_LEFT || position === SeatLayoutPosition.TOP_RIGHT) &&
      status === SeatLayoutStatus.WALKWAY_ABOVE
    ) {
      const updated = [...seats];

      const seat = { ...updated[selectedSeatIndex] };
      seat.walkway = [...(seat.walkway ?? [])];

      if (!seat.walkway.includes(WALKWAY_VALUE.IN_LEFT)) {
        seat.walkway.push(WALKWAY_VALUE.IN_LEFT);
      }

      updated[selectedSeatIndex] = seat;

      // setSeats(updated);

      updateMutation(
        {
          resource: "content/seat-maps/seats",
          id: seatId,
          meta: {
            ignoreResourceWrapper: true
          },
          values: {
            status: selectedSeat?.status,
            walkway: updated[selectedSeatIndex]?.walkway
          },
        },
        {
          onSuccess: (data) => {
            refetch();
            toast.success("Add seat walkway successfully");
          },
        }
      );

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.TOP_LEFT || position === SeatLayoutPosition.TOP_RIGHT) &&
      status === SeatLayoutStatus.WALKWAY_BELOW
    ) {
      const updated = [...seats];

      const seat = { ...updated[selectedSeatIndex] };
      seat.walkway = [...(seat.walkway ?? [])];

      if (!seat.walkway.includes(WALKWAY_VALUE.IN_RIGHT)) {
        seat.walkway.push(WALKWAY_VALUE.IN_RIGHT);
      }

      updated[selectedSeatIndex] = seat;

      // setSeats(updated);

      updateMutation(
        {
          resource: "content/seat-maps/seats",
          id: seatId,
          meta: {
            ignoreResourceWrapper: true
          },
          values: {
            status: selectedSeat?.status,
            walkway: updated[selectedSeatIndex]?.walkway
          },
        },
        {
          onSuccess: (data) => {
            refetch();
            toast.success("Add seat walkway successfully");
          },
        }
      );

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.BOTTOM_LEFT || position === SeatLayoutPosition.BOTTOM_RIGHT) &&
      status === SeatLayoutStatus.WALKWAY_ABOVE
    ) {
      const updated = [...seats];

      const seat = { ...updated[selectedSeatIndex] };
      seat.walkway = [...(seat.walkway ?? [])];

      if (!seat.walkway.includes(WALKWAY_VALUE.IN_LEFT)) {
        seat.walkway.push(WALKWAY_VALUE.IN_LEFT);
      }

      updated[selectedSeatIndex] = seat;

      // setSeats(updated);

      updateMutation(
        {
          resource: "content/seat-maps/seats",
          id: seatId,
          meta: {
            ignoreResourceWrapper: true
          },
          values: {
            status: selectedSeat?.status,
            walkway: updated[selectedSeatIndex]?.walkway
          },
        },
        {
          onSuccess: (data) => {
            refetch();
            toast.success("Add seat walkway successfully");
          },
        }
      );

      return;
    }

    if (
      selectedSeatIndex > -1 &&
      (position === SeatLayoutPosition.BOTTOM_LEFT || position === SeatLayoutPosition.BOTTOM_RIGHT) &&
      status === SeatLayoutStatus.WALKWAY_BELOW
    ) {
      const updated = [...seats];

      const seat = { ...updated[selectedSeatIndex] };
      seat.walkway = [...(seat.walkway ?? [])];

      if (!seat.walkway.includes(WALKWAY_VALUE.IN_RIGHT)) {
        seat.walkway.push(WALKWAY_VALUE.IN_RIGHT);
      }

      updated[selectedSeatIndex] = seat;

      // setSeats(updated);

      updateMutation(
        {
          resource: "content/seat-maps/seats",
          id: seatId,
          meta: {
            ignoreResourceWrapper: true
          },
          values: {
            status: selectedSeat?.status,
            walkway: updated[selectedSeatIndex]?.walkway
          },
        },
        {
          onSuccess: (data) => {
            refetch();
            toast.success("Add seat walkway successfully");
          },
        }
      );

      return;
    }

    updateMutation(
      {
        resource: "content/seat-maps/seats",
        id: seatId,
        meta: {
          ignoreResourceWrapper: true
        },
        values: {
          status: status,
          walkway: selectedSeat?.walkway
        },
      },
      {
        onSuccess: (data) => {
          refetch();
          toast.success("Update seat status successfully");
        },
      }
    );
  };


  const seatLayoutDetailData = seatLayoutDetail?.data ?? null;

  if (isLoadingLayoutDetail && open) {
    return <FullScreenLoading />;
  }

  return (
    <Modal 
      title={`${!isEmpty(id) ? "Edit" : "Add"} seat layout info`} 
      onClose={onClose} open={open}
      closeOnClickBackdrop={false}
    >
      <div>
        <div className="w-full divide-y divide-gray-100 text-sm">
          <div className="box-container">
            <div className="grid sm:grid-cols-8 gap-y-4 gap-x-4 w-full mx-auto">
              <div className="col-span-full sm:col-span-2">
                <div className="flex flex-col items-start gap-x-4">
                  <Label name="Row name" id="row_name_type" hint="Configure the seat row name" required />
                  <Controller
                    control={control}
                    name="row_name_type"
                    render={({ field: { onChange, value = "", ref } }) => {
                      return (
                        <div className="flex flex-col gap-2">
                          {SEAT_ROW_NAME_TYPES.map(type => (
                            <RadioButton
                              disabled={isOpenRegistration(eventDetailData.open_registration_date)}
                              checked={type.value === value}
                              onChange={() => {
                                onChange(type.value);
                              }}
                              item={{
                                id: type.value,
                                title: type.label,
                              }}
                              key={type.value}
                            />
                          ))}
                        </div>
                      );
                    }}
                  />
                  {get(errors, `row_name_type`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `row_name_type.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Role name position */}
              <div className="col-span-full sm:col-span-6">
                <div className="flex flex-col gap-x-4">
                  <Label name="Row name position" id="row_name_position" required />
                  <Controller
                    control={control}
                    name="row_name_position"
                    render={({ field, fieldState }) => {
                      return (
                        <div className="mt-1">
                          <Select
                            isDisabled={isOpenRegistration(eventDetailData.open_registration_date)}
                            error={fieldState.error}
                            options={SEAT_POSITION}
                            id="row_name_position"
                            value={SEAT_POSITION.filter((item) =>
                              field.value.includes(item.value)
                            )}
                            placeholder="Select role name position"
                            onChange={(selected: {
                              label: string;
                              value: string;
                            }) => {
                              field.onChange(selected.value);
                              handleResetWalkway();
                            }}
                          />
                        </div>
                      );
                    }}
                  />
                  {get(errors, `row_name_position`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `row_name_position.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="sm:col-span-2">
                <Label 
                  id="layout_name" 
                  name="Layout name"
                  required
                />
                <Input 
                  id="layout_name"
                  type="text"
                  placeholder="Layout name"
                  error={errors["layout_name"]}
                  {...register("layout_name")}
                />
                {errors["layout_name"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["layout_name"]?.message}
                    </span>
                  </p> 
                ): null}
              </div>
              <div className="sm:col-span-2">
                <Label
                  required
                  id="number_of_columns"
                  name="Number of columns"
                />
                <Input
                  id="number_of_columns"
                  type="number"
                  min={1}
                  placeholder="Number of columns"
                  disabled={isOpenRegistration(eventDetailData.open_registration_date)}
                  {...register("number_of_columns", {
                    valueAsNumber: true,
                  })}
                  error={errors["number_of_columns"]}
                />
                {errors["number_of_columns"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["number_of_columns"]?.message}
                    </span>
                  </p> 
                ): null}
              </div>
              <div className="sm:col-span-2">
                <Label
                  required
                  id="number_of_rows"
                  name="Number of rows"
                />
                <Input
                  id="number_of_rows"
                  type="number"
                  min={1}
                  placeholder="Number of rows"
                  disabled={isOpenRegistration(eventDetailData.open_registration_date)}
                  {...register("number_of_rows", {
                    valueAsNumber: true,
                  })}
                  error={errors["number_of_rows"]}
                />
                {errors["number_of_rows"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["number_of_rows"]?.message}
                    </span>
                  </p> 
                ): null}
              </div>
              <div className="sm:col-span-2">
                <div className="invisible">
                  <Label
                    required
                    id="refresh_button"
                    name="Refresh Button"
                  />
                </div>
                <Button className="w-full border border-solid border-brand-500" onClick={handleSubmit(_submit)} disabled={isUpdating}>
                  Update layout
                </Button>
              </div>
              <div className="col-span-full">
                <div className="flex flex-col gap-y-4">
                  <div className="w-full flex flex-wrap sm:justify-center sm:items-center gap-4 mt-6 mx-auto">
                    {SEAT_STATUS.map(status => {
                      if (
                        status.value === SeatLayoutStatus.WALKWAY_ON_THE_LEFT ||
                        status.value === SeatLayoutStatus.WALKWAY_ON_THE_RIGHT ||
                        status.value === SeatLayoutStatus.WALKWAY_ABOVE ||
                        status.value === SeatLayoutStatus.WALKWAY_BELOW
                      ) {
                        return null;
                      }

                      if (status.value === "hide") {
                        return (
                          <div
                            key={status.value}
                            className="flex items-center gap-x-2"
                          >
                            <div
                              className="relative flex items-center justify-center size-6 border border-solid font-medium rounded-full select-none
                                          border-[#CCD3FF] text-black bg-[#CCD3FF]"
                            ></div>
                            {status.label}
                          </div>
                        )
                      }

                      if (status.value === "walkway") {
                        return (
                          <div
                            key={status.value}
                            className="flex items-center gap-x-2"
                          >
                            <div
                              className="relative flex items-center justify-center size-6 border border-solid font-medium rounded-full select-none
                                          border-[#868E95] text-black bg-[#868E95]"
                            ></div>
                            {status.label}
                          </div>
                        )
                      }

                      if (status.value === "in_cart") {
                        return (
                          <div
                            key={status.value}
                            className="flex items-center gap-x-2"
                          >
                            <div
                              className="relative flex items-center justify-center size-6 border border-solid font-medium rounded-full select-none
                                          border-[#F7A06E] text-black bg-[#F7A06E]"
                            ></div>
                            {status.label}
                          </div>
                        )
                      }

                      return (
                        <div key={status.value} className="flex items-center gap-x-2 shrink-0">
                          <div 
                            className={twMerge("relative flex items-center justify-center size-6 border border-solid font-medium rounded-full select-none", 
                              status.value === SeatLayoutStatus.INACTIVE 
                              ? "border-gray-300 text-black bg-gray-300" 
                              : status.value === SeatLayoutStatus.SOLD_OUT 
                                ? "border-brand-500 text-black bg-brand-500" 
                                : "border-brand-500 text-brand-500 bg-white")}
                          >
                            {status.value === SeatLayoutStatus.UNAVAILABLE ? <XIcon className="size-4 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" /> : ""}
                          </div>
                          {status.label}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <SeatmapTable isEdit={true} rowNameType={seatLayoutDetailData?.row_name_type ?? ""} rowNamePosition={watch("row_name_position")} tableData={seats} _updateSeatStatus={_updateSeatStatus} handleRemoveWalkway={handleRemoveWalkway} />
            </div>
          </div>
        </div>
      </div>
      {(isFetchingLayoutDetail || isUpdating) ? <FullScreenLoading /> : <></>}
    </Modal>
  )
};
